import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { fetchWithAuth } from '../utils/api';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Award, ArrowLeft, AlertTriangle, RefreshCw, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';

// Complete implementation of formatNumberSafely function
const formatNumberSafely = (value: any, decimals = 1): string => {
  // If value is null, undefined, or an empty string
  if (value === null || value === undefined || value === '') return 'N/A';
  
  // Convert to number if it isn't already
  const num = typeof value === 'number' ? value : Number(value);
  
  // Check if it's a valid number after conversion
  if (isNaN(num)) return 'N/A';
  
  // For zero values (may appear as "0.0")
  if (num === 0) return '0.0';
  
  // Return formatted number with specified decimals
  return num.toFixed(decimals);
};

// Add this prop to the RoundResultsPageProps interface
interface RoundResultsPageProps {
  useSimpleEndpoint?: boolean;
  useRobustEndpoint?: boolean;
}

// Update the component to accept the prop
const RoundResultsPage: React.FC<RoundResultsPageProps> = ({ 
  useSimpleEndpoint = false,
  useRobustEndpoint = false
}) => {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const { pageTransition } = useTheme();

  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [expandedSummary, setExpandedSummary] = useState(false);
  const [expandedComments, setExpandedComments] = useState<{ [key: number]: boolean }>({});

  // Helper function to toggle comment expansion
  const toggleCommentExpansion = (speakerId: number) => {
    setExpandedComments(prev => ({
      ...prev,
      [speakerId]: !prev[speakerId]
    }));
  };

  // Update fetchResults to use the robust endpoint when specified
  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let response = null;
      let primaryEndpointFailed = false;

      // Determine which endpoint to try first
      let primaryEndpoint = `/api/round/${roundId}/results/`;
      
      if (useRobustEndpoint) {
        primaryEndpoint = `/api/round/${roundId}/results/`;
      } else if (useSimpleEndpoint) {
        primaryEndpoint = `/api/round/${roundId}/simple-round-results/`;
      }
      
      console.log(`Using endpoint: ${primaryEndpoint}`);
      
      // First, try the primary endpoint
      try {
        response = await fetchWithAuth(primaryEndpoint);
      } catch (err: any) {
        console.error(`Failed with primary endpoint: ${err}`);
        primaryEndpointFailed = true;
        // Primary endpoint failed, try fallbacks
      }
      
      // If the primary endpoint failed or we got no response, try alternatives
      if (!response) {
        // Try alternative endpoints
        const fallbackEndpoints = [
          `/api/round/${roundId}/robust-results/`,
          `/round/${roundId}/robust-results/`,
          `/api/round/${roundId}/simple-round-results/`,
          `/round/${roundId}/simple-round-results/`,
          `/api/round/${roundId}/results/`,
          `/round/${roundId}/results/`
        ];
        
        // Skip endpoints we've already tried
        const endpointsToTry = fallbackEndpoints.filter(e => e !== primaryEndpoint);
        
        console.log(`Primary endpoint ${primaryEndpointFailed ? 'failed' : 'returned no data'}, trying fallbacks`);
        
        for (const endpoint of endpointsToTry) {
          try {
            console.log(`Trying fallback endpoint: ${endpoint}`);
            response = await fetchWithAuth(endpoint);
            if (response) {
              console.log(`Fallback endpoint successful: ${endpoint}`);
              break;
            }
          } catch (err) {
            console.warn(`Fallback endpoint failed: ${endpoint}`, err);
          }
        }
      }

      if (response) {
        // Format the data for consistency
        const formattedResponse = {
          id: roundId,
          status: 'completed',
          ...response
        };

        setResults(formattedResponse);
      } else {
        // No endpoint worked, show error
        setError("Could not load round results");
        
        // Create a minimal fallback response
        setResults({
          id: roundId,
          status: 'completed',
          winning_side: null,
          summary: "Results are not available at this time.",
          submitted_by: null,
          speaker_scores: [],
          participants: []
        });
        
        toast.error("Could not load complete results");
      }
    } catch (err: any) {
      console.error("Error fetching round results:", err);
      setError(err.message || "Failed to fetch round results");
      
      // Create a minimal result object so the page can still render something
      setResults({
        id: roundId,
        status: 'completed',
        winning_side: null,
        summary: "Results are not available at this time.",
        submitted_by: null,
        speaker_scores: [],
        participants: []
      });
      
      toast.error("Could not load complete results");
    } finally {
      setLoading(false);
    }
  }, [roundId, useSimpleEndpoint, useRobustEndpoint]);
  
  useEffect(() => {
    fetchResults();
  }, [fetchResults, retryCount]);

  useEffect(() => {
    if (results) {
      console.log("API Response:", results);
      console.log("Speaker scores:", results.speaker_scores);
      console.log("Adjudicator ratings:", results.adjudicator_ratings);
    }
  }, [results]);
  
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchResults();
  };

  const handleBack = () => {
    pageTransition(() => {
      navigate('/dashboard');
    });
  };

  // Helper function to render summary with expand/collapse functionality
  const renderSummary = (summary: string) => {
    if (!summary) return "No summary provided";
    
    const isSummaryLong = summary.length > 100;
    
    if (!isSummaryLong) return summary;
    
    return (
      <div>
        <p>
          {expandedSummary ? summary : `${summary.substring(0, 100)}...`}
        </p>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setExpandedSummary(!expandedSummary)}
          className="mt-2 text-xs flex items-center"
        >
          {expandedSummary ? (
            <>Show Less <ChevronUp className="ml-1 h-3 w-3" /></>
          ) : (
            <>Show More <ChevronDown className="ml-1 h-3 w-3" /></>
          )}
        </Button>
      </div>
    );
  };

  // Helper function to render speaker comments with expand/collapse functionality
  const renderComments = (speaker: any) => {
    if (!speaker.comments) return "";
    
    const isCommentLong = speaker.comments.length > 50;
    const speakerId = speaker.user_id || speaker.speaker_id;
    const isExpanded = expandedComments[speakerId] || false;
    
    if (!isCommentLong) return speaker.comments;
    
    return (
      <div>
        <p>
          {isExpanded ? speaker.comments : `${speaker.comments.substring(0, 50)}...`}
        </p>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => toggleCommentExpansion(speakerId)}
          className="mt-1 text-xs flex items-center p-0 h-6"
        >
          {isExpanded ? (
            <>Show Less <ChevronUp className="ml-1 h-3 w-3" /></>
          ) : (
            <>Show More <ChevronDown className="ml-1 h-3 w-3" /></>
          )}
        </Button>
      </div>
    );
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5 } },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[50vh]">
        <div className="animate-pulse">
          <p className="text-xl font-medium">Loading results...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="p-4 md:p-6 space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Round Results</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Final results and speaker scores
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchResults} className="md:self-end">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleBack} className="md:self-end">
            <ArrowLeft size={16} className="mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-amber-500 bg-amber-500 bg-opacity-10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              <p className="text-amber-600 dark:text-amber-400">
                {error}
              </p>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Some results may be incomplete. You can try refreshing the page.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={handleRetry}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {results && (
        <>
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader className="pb-0">
                <div className="flex items-center gap-3">
                  <Award size={24} className="text-amber-500" />
                  <CardTitle>Final Decision</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Winning Side</p>
                    <p className="text-xl font-bold">
                      {results.winning_side ? (
                        results.format === 'BP' ? (
                          <span className={getTeamColor(results.winning_side)}>
                            {formatWinningSide(results.winning_side)}
                          </span>
                        ) : (
                          results.winning_side === 'GOVERNMENT' ? (
                            <span className="text-blue-600 dark:text-blue-400">Government</span>
                          ) : (
                            <span className="text-red-600 dark:text-red-400">Opposition</span>
                          )
                        )
                      ) : (
                        <span className="text-gray-500">Not Available</span>
                      )}
                    </p>
                  </div>

                  {results.submitted_by && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Submitted By</p>
                      <p className="font-medium">{results.submitted_by.username}</p>
                    </div>
                  )}
                </div>

                {/* Add Motion display */}
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Motion</p>
                  <p className="mt-1 font-medium">
                    {typeof results.motion === 'object' 
                      ? results.motion.text 
                      : results.motion || "No motion provided"}
                  </p>
                </div>

                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Summary</p>
                  <div className="mt-1">{renderSummary(results.summary || "No summary provided")}</div>
                </div>
                
                {/* Show user's role for context */}
                {results.user_role && (
                  <div className="mt-4 p-2 bg-gray-100 dark:bg-gray-800 rounded-md">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      You are viewing this as: <span className="font-medium">{results.user_role}</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Team Rankings - Only visible to chair and non-debaters */}
          {results.format === 'BP' && results.team_rankings && 
          Object.keys(results.team_rankings).length > 0 && 
          (results.user_role === 'Chair Adjudicator' || 
           !['PM', 'DPM', 'LO', 'DLO', 'MG', 'GW', 'MO', 'OW'].includes(results.user_role || '')) && (
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader className="pb-0">
                  <div className="flex items-center gap-3">
                    <Award size={24} className="text-amber-500" />
                    <CardTitle>Team Rankings</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Team</TableHead>
                        <TableHead className="text-right">Rank</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(results.team_rankings)
                        .sort(([_team1, rankA], [_team2, rankB]) => (rankA as number) - (rankB as number))
                        .map(([team, rank], index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{team}</TableCell>
                            <TableCell className="text-right">
                              <span className={`font-bold ${
                                rank === 1 ? 'text-amber-500' :
                                rank === 2 ? 'text-blue-500' :
                                rank === 3 ? 'text-purple-500' :
                                rank === 4 ? 'text-gray-600 dark:text-gray-400' :
                                'text-gray-500'
                              }`}>
                                {String(rank)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Speaker Scores section */}
          {results.speaker_scores && results.speaker_scores.length > 0 ? (
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader className="pb-0">
                  <CardTitle>Speaker Scores</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Speaker</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead className="text-right">Score</TableHead>
                          <TableHead>Speaking Time</TableHead>
                          <TableHead>Comments</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {results.speaker_scores.map((speaker: any, index: number) => (
                          <TableRow key={speaker.user_id || index}>
                            <TableCell className="font-medium">{speaker.username || 'Unknown'}</TableCell>
                            <TableCell>{speaker.role || 'Unknown'}</TableCell>
                            <TableCell className="text-right">
                              <span className="font-medium">
                                {formatNumberSafely(speaker.score)}
                              </span>
                            </TableCell>
                            <TableCell>
                              {speaker.speaking_time ? (
                                <div className="flex items-center">
                                  <Clock size={14} className="mr-1 text-gray-500" />
                                  {speaker.speaking_time}
                                </div>
                              ) : ''}
                            </TableCell>
                            <TableCell>{renderComments(speaker)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            results.participants && results.participants.length > 0 && (
              <motion.div variants={itemVariants}>
                <Card>
                  <CardHeader className="pb-0">
                    <CardTitle>Debaters</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Participant</TableHead>
                            <TableHead>Role</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {results.participants
                            .filter((participant: any) => 
                              !['Chair Adjudicator', 'Panelist', 'Trainee', 'Spectator'].includes(participant.role)
                            )
                            .map((participant: any, index: number) => (
                              <TableRow key={participant.user_id || index}>
                                <TableCell className="font-medium">{participant.username}</TableCell>
                                <TableCell>{participant.role}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )
          )}

          {/* Adjudicator Ratings section */}
          {results.adjudicator_ratings && results.adjudicator_ratings.length > 0 ? (
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader className="pb-0">
                  <CardTitle>Adjudicator Ratings</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Adjudicator</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead className="text-right">Average Rating</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {results.adjudicator_ratings.map((adjudicator: any, index: number) => (
                          <TableRow key={adjudicator.user_id || adjudicator.adjudicator_id || index}>
                            <TableCell className="font-medium">{adjudicator.username || 'Unknown'}</TableCell>
                            <TableCell>{adjudicator.role || 'Unknown'}</TableCell>
                            <TableCell className="text-right">
                              <span className="font-medium">
                                {formatNumberSafely(adjudicator.average_rating)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            results.participants && results.participants.length > 0 && (
              <motion.div variants={itemVariants}>
                <Card>
                  <CardHeader className="pb-0">
                    <CardTitle>Adjudicators</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Participant</TableHead>
                            <TableHead>Role</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {results.participants
                            .filter((participant: any) => 
                              ['Chair Adjudicator', 'Panelist', 'Trainee'].includes(participant.role)
                            )
                            .map((participant: any, index: number) => (
                              <TableRow key={participant.user_id || index}>
                                <TableCell className="font-medium">{participant.username}</TableCell>
                                <TableCell>{participant.role}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )
          )}
        </>
      )}
    </motion.div>
  );
};

// Helper function to get the appropriate color for a team
function getTeamColor(team: string) {
  if (!team) return "text-gray-500";
  if (team.includes("GOVERNMENT") || team.toLowerCase().includes('government'))
    return "text-blue-600 dark:text-blue-400";
  if (team.includes("OPPOSITION") || team.toLowerCase().includes('opposition'))
    return "text-red-600 dark:text-red-400";
  return "text-gray-500";
}

// Helper function to format the winning side text
function formatWinningSide(winningSide: string) {
  if (!winningSide) return "Not Available";

  // Format strings like "OPENING_GOVERNMENT" to "Opening Government"
  return winningSide
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

export default RoundResultsPage;